import { useParams } from "react-router-dom";
import { Card } from "@/shared/components/ui/card";
import { KpiTile } from "@/shared/components/ui/kpi";
import { InfoHint } from "@/shared/components/ui/info-hint";
import { useCampaignsList } from "../use-campaigns";
import { useLeadCounts } from "../use-leads";
import { useCampaignAnalytics } from "../use-analytics";

const FUNNEL_MAX_PX = 260;
const STEP_MAX_PX = 220;

const STEP_SERIES = [
  { key: "opened" as const, label: "Open", color: "var(--ge-chart-1)" },
  { key: "clicked" as const, label: "Click", color: "var(--ge-chart-2)" },
  { key: "replied" as const, label: "Reply", color: "var(--ge-chart-3)" },
];

/**
 * Analytics (screens.md §10) — real funnel + KPIs from the campaign's own data,
 * and real per-step engagement from GET /campaigns/:id/analytics (unique
 * enrollments). The per-step chart is empty until delivery-event webhooks flow.
 */
export function AnalyticsTab() {
  const { id } = useParams<{ id: string }>();
  const list = useCampaignsList();
  const leadsQ = useLeadCounts(id ?? "");
  const analyticsQ = useCampaignAnalytics(id ?? "");

  const summary = list.data?.find((c) => c.id === id);
  const discovered = leadsQ.data?.total ?? 0;
  const approved = leadsQ.data?.counts?.APPROVED ?? 0;
  const contacts = summary?.contacts ?? 0;
  const sent = summary?.sent ?? 0;
  const opened = summary?.opened ?? 0;
  const opensTotal = summary?.opensTotal ?? 0;
  const openRate = sent > 0 ? `${Math.round((opened / sent) * 100)}%` : "--";

  const totals = analyticsQ.data?.totals;
  const byStep = analyticsQ.data?.byStep ?? [];

  const funnel = [
    { label: "Discovered", value: discovered },
    { label: "Approved", value: approved },
    { label: "Contacts", value: contacts },
    { label: "Sent", value: sent, accent: false },
    { label: "Opened", value: opened, accent: true },
  ];
  const funnelMax = Math.max(1, ...funnel.map((r) => r.value));

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <KpiTile label="Sent" value={summary ? String(sent) : "--"} />
        <KpiTile label="Open rate" value={openRate} highlighted />
        <KpiTile label="Total opens" value={summary ? String(opensTotal) : "--"} />
        <KpiTile label="Replies" value={totals ? String(totals.replied) : "--"} />
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        {/* Funnel — real */}
        <Card className="overflow-x-auto p-[18px]">
          <p className="ge-eyebrow flex items-center gap-1">FUNNEL <InfoHint text="This campaign's outreach funnel — sent, opened, clicked, replied. Every count is unique recipients (not raw events)." /></p>
          <div className="mt-4 flex min-w-[380px] flex-col gap-[10px]">
            {funnel.map((r) => (
              <div key={r.label} className="flex items-center gap-[10px]">
                <div className="w-[92px] shrink-0 text-right font-mono text-[11px] text-ge-secondary">
                  {r.label}
                </div>
                <div
                  className="h-4 rounded-r-[4px]"
                  style={{
                    width: `${(r.value / funnelMax) * FUNNEL_MAX_PX}px`,
                    background: r.accent ? "var(--ge-accent)" : "var(--ge-chart-1)",
                    minWidth: r.value > 0 ? "2px" : "0",
                  }}
                />
                <div className={`font-mono text-[12px] ${r.accent ? "text-ge-accent" : "text-ge-primary"}`}>
                  {r.value}
                </div>
              </div>
            ))}
          </div>
          <p className="mt-4 text-[12px] text-ge-muted">
            The acquisition funnel from discovery to opens. Open rate counts unique recipients; total
            opens counts every re-open (a hesitation/interest signal).
          </p>
        </Card>

        {/* Per-step — real, from the analytics endpoint */}
        <Card className="overflow-x-auto p-[18px]">
          <div className="flex items-center">
            <p className="ge-eyebrow flex items-center gap-1">BY STEP <InfoHint text="Engagement broken down by sequence step, so you can see which follow-up in the drip is pulling its weight." /></p>
            <div className="ml-auto flex items-center gap-[14px]">
              {STEP_SERIES.map((s) => (
                <div key={s.key} className="flex items-center gap-[6px]">
                  <span className="h-2 w-2 rounded-[2px]" style={{ background: s.color }} />
                  <span className="text-[11.5px] text-ge-secondary">{s.label}</span>
                </div>
              ))}
            </div>
          </div>

          {byStep.length === 0 ? (
            <p className="mt-4 text-[12.5px] text-ge-secondary" data-testid="bystep-empty">
              No engagement yet. Once the sequence is launched and delivery events arrive (webhooks),
              per-step open/click/reply rates show here.
            </p>
          ) : (
            <div className="mt-4 flex min-w-[360px] flex-col gap-[14px]" data-testid="bystep">
              {byStep.map((row) => (
                <div key={row.order} className="flex items-center gap-[10px]">
                  <div className="w-16 shrink-0 text-right font-mono text-[11px] text-ge-secondary">
                    Step {row.order}
                  </div>
                  <div className="flex flex-1 flex-col gap-[2px]">
                    {STEP_SERIES.map((s) => {
                      const pct = row.sent > 0 ? Math.round((row[s.key] / row.sent) * 100) : 0;
                      return (
                        <div key={s.key} className="flex items-center gap-2">
                          <div
                            className="h-[10px] rounded-r-[4px]"
                            style={{ width: `${(pct / 100) * STEP_MAX_PX}px`, background: s.color, minWidth: pct > 0 ? "2px" : "0" }}
                          />
                          <span className="font-mono text-[10.5px] text-ge-secondary">{pct}%</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
          <p className="mt-4 text-[12px] text-ge-muted">
            Per-step engagement (unique recipients) — cut the step that underperforms, keep what converts.
          </p>
        </Card>
      </div>
    </div>
  );
}
